import React, { useState, useCallback, Fragment } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getNavItems } from 'selectors/navigationSelectors'
import NavigationMenuItem from './NavigationMenuItem'
import SlideDown from 'components/Common/SlideDown'
import { rem } from 'polished'
import { accountAccessShowLogin } from 'actions/UiActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import NavigationPhoneMenu from './NavigationPhoneMenu'
import NavigationRegionMenu from './NavigationRegionMenu'
import { hasAccountNotification, isLoggedIn } from 'selectors/accountSelectors'
import AvatarCircle from 'components/Common/AvatarCircle/AvatarCircle'
import FormatCurrency from 'components/Common/FormatCurrency'
import ListItem from 'components/Luxkit/List/ListItem'
import config from 'constants/config'
import BusinessTravellerAccountListItemMenu from 'businessTraveller/components/business-account/BusinessTravellerAccountListItemMenu'
import Modal from 'components/Luxkit/Modal/Modal'
import Group from 'components/utils/Group'
import Caption from 'components/Luxkit/Typography/Caption'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { isActiveLuxuryPlusMember } from 'loyalty/selectors/featureToggle'
import { LUXURY_PLUS } from 'loyalty/constants/base'
import { themeClassName } from 'lib/theme/themeUtils'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'

const NavContainer = styled.nav`
  display: block;
`

const LoginBlock = styled.div`
  > * + * {
    margin-left: ${rem(12)};
  }
`

interface Props {
  navItems: Array<App.NavItem>;
  isLoggedIn?: boolean;
  hasNotification: boolean;
  firstName?: string;
  lastName?: string;
  balance: number;
  isActiveLuxuryPlusMember: boolean;
}

function NavigationMenu(props: Props) {
  const {
    navItems,
    hasNotification,
    isLoggedIn,
    firstName,
    lastName,
    balance,
    isActiveLuxuryPlusMember,
  } = props
  const [openNav, setOpenNav] = useState<string>()

  const dispatch = useAppDispatch()
  const modalElement = useModalElementContext<App.NavItem | undefined>()
  const onNavToggle = useCallback((navItem: App.NavItem) => {
    setOpenNav(openNav === navItem.id ? '' : navItem.id)
    if (!navItem.hasDropdown) {
      modalElement.resolve(navItem)
    }
  }, [modalElement, openNav])

  const showLogin = useCallback(() => {
    dispatch(accountAccessShowLogin())
    modalElement.resolve(undefined)
  }, [dispatch, modalElement])

  const onModalClose = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  return <Modal title="" mode="menu" height="full" className={themeClassName('inverse')}>
    <Group direction="vertical" gap={20} verticalAlign="space-between" fullHeight>
      <Group direction="vertical" gap={20}>
        <NavigationPhoneMenu onClick={onModalClose} />
        <NavContainer role="menu" data-testid="NavigationMenu">
          {navItems.map((nav) =>
            <Fragment key={nav.id}>
              <NavigationMenuItem navItem={nav} onClick={onNavToggle} isOpen={openNav === nav.id} />
              {nav.items && nav.items.length > 0 && <SlideDown show={openNav === nav.id}>
                {nav.items?.map((item) => (
                  <NavigationMenuItem
                    data-testid="NaviationMenuItem"
                    key={item.id}
                    navItem={item}
                    onClick={onNavToggle}
                  />
                ))}
              </SlideDown>}
            </Fragment>,
          )}
          <NavigationRegionMenu />
        </NavContainer>
        {isLoggedIn && config.businessTraveller.isEnabled && <BusinessTravellerAccountListItemMenu />}
        {isLoggedIn && !config.businessTraveller.isEnabled && (
          <ListItem
            onClick={() => modalElement.resolve(undefined)}
            to="/account"
            title={isActiveLuxuryPlusMember ? LUXURY_PLUS.PROGRAM_NAME : 'Account'}
            subtitle={balance > 0 ? <FormatCurrency value={balance} format="dynamicDollar" /> : undefined }
            aria-label="Account"
            titleColour="loyalty"
            startIcon={
              <ProductPaletteProvider palette={isActiveLuxuryPlusMember ? 'loyalty' : 'default'}>
                <AvatarCircle
                  firstName={firstName}
                  lastName={lastName}
                  size="small"
                  variant="dark"
                  nonInteractable
                  hasNotification={hasNotification}
                />
              </ProductPaletteProvider>
            }
          />
        )}
        {!isLoggedIn && (
          <LoginBlock>
            <TextButton
          kind="primary"
          variant="ghost"
          onClick={showLogin}
          role="menuitem"
          >
              Sign Up
            </TextButton>
            <TextButton
              kind="secondary"
              variant="ghost"
              onClick={showLogin}
              role="menuitem"
            >
              Log in
            </TextButton>
          </LoginBlock>
        )}
      </Group>

      {config.MOBILE_APP_CUSTOMISATIONS && config.MOBILE_APP_VERSION && (
        <Group direction="horizontal" horizontalAlign="end">
          <Caption variant="large">v{config.MOBILE_APP_VERSION}</Caption>
        </Group>
      )}
    </Group>
  </Modal>
}

function mapStateToProps(state: App.State) {
  return {
    navItems: getNavItems(state),
    isLoggedIn: isLoggedIn(state),
    hasNotification: hasAccountNotification(state),
    firstName: state.auth.account.givenName,
    lastName: state.auth.account.surname,
    balance: state.auth.account.balance,
    isActiveLuxuryPlusMember: isActiveLuxuryPlusMember(state),
  }
}

export default connect(mapStateToProps)(NavigationMenu)
